import { bestsellersSource, type BestsellerEntry } from "@/domain/bestsellers";
import { getMerchantId } from "@/lib/company-storage";
import { useEffect, useState } from "react";

/**
 * Results already read, per store — avoids repeat calls when the hook mounts
 * more than once for the same store in one visit.
 */
const cache = new Map<string, BestsellerEntry[]>();

/** Promessa em voo por loja — dedup de corridas paralelas contra o mesmo id. */
const inFlight = new Map<string, Promise<BestsellerEntry[]>>();

function fetchOnce(merchantId: string): Promise<BestsellerEntry[]> {
  const cached = cache.get(merchantId);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(merchantId);
  if (pending) return pending;

  const promise = bestsellersSource.get(merchantId).then((entries) => {
    cache.set(merchantId, entries);
    inFlight.delete(merchantId);
    return entries;
  });
  inFlight.set(merchantId, promise);
  return promise;
}

/**
 * Current store bestsellers ranking (`GET /merchants/{id}/bestsellers`).
 * Fetches ONCE per store per page load. `enabled` skips the fetch when the
 * section is not bestsellers. Never throws — empty list while in flight.
 */
export function useBestsellers(enabled: boolean): BestsellerEntry[] {
  const merchantId = getMerchantId();
  const [entries, setEntries] = useState<BestsellerEntry[]>(() => (merchantId ? (cache.get(merchantId) ?? []) : []));

  useEffect(() => {
    if (!enabled || !merchantId) return;

    const cached = cache.get(merchantId);
    if (cached) {
      setEntries(cached);
      return;
    }

    let cancelled = false;
    fetchOnce(merchantId).then((result) => {
      if (!cancelled) setEntries(result);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, merchantId]);

  return entries;
}

/** Tests only: forget what was already read between cases. */
export function resetBestsellersCache(): void {
  cache.clear();
  inFlight.clear();
}
